import { isCustomFieldEmpty } from './custom-field-empty.js';

/**
 * MAP (in-file navigation graph — keep in sync with code):
 *   role:      pure util — read-only presentation of a custom-field list (UI nuova record pages): filled fields in
 *              their order, with fields sharing a short UPPERCASE label prefix («GEN - Confidenza», «FON - Salita»…)
 *              folded into one group when the prefix has at least `minGroup` filled fields; empty fields apart
 *   exports:   customFieldLayout(fields, { minGroup = 3 }) → { entries, empty }
 *              entries: [{ field } | { group: { prefix, rows: [{ label, field }] } }] — a group sits where its first
 *              filled member was; empty: the empty fields in their order (rendered behind a toggle)
 *   uses:      lib/custom-field-empty.js (isCustomFieldEmpty — the same «empty» the value renderer prints «—» for)
 *   used by:   helpers/custom-field-layout.js
 *   tests:     tests/unit/lib/custom-field-layout-test.js
 *   gotchas:   (1) Disabled organization fields are dropped: ViewCustomField renders nothing for them, so they are
 *              neither a row nor an «empty» count. (2) Input may be any iterable-ish (array, ManyArray): read with
 *              forEach / Array.from, never .filter on a PromiseManyArray (await it first, e.g. the `load` helper).
 */
const PREFIX_RE = /^([A-Z]{2,6})\s+-\s+(.+)$/;
function toArray(fields) {
  if (!fields) return [];
  if (Array.isArray(fields)) return fields;
  if (typeof fields.toArray === 'function') return fields.toArray();
  const out = [];
  if (typeof fields.forEach === 'function') fields.forEach(f => out.push(f));
  return out;
}
function splitPrefix(name) {
  const m = PREFIX_RE.exec(String(name ?? '').trim());
  return m ? {
    prefix: m[1],
    rest: m[2].trim()
  } : null;
}
function customFieldLayout(fields, {
  minGroup = 3
} = {}) {
  const filled = [];
  const empty = [];
  toArray(fields).forEach(field => {
    if (!field || field.organization_custom_field?.disabled) return;
    (isCustomFieldEmpty(field) ? empty : filled).push(field);
  });
  const counts = new Map();
  const parts = filled.map(field => {
    const split = splitPrefix(field.organization_custom_field?.name);
    if (split) counts.set(split.prefix, (counts.get(split.prefix) || 0) + 1);
    return split;
  });
  const entries = [];
  const groups = new Map();
  filled.forEach((field, i) => {
    const split = parts[i];
    if (split && counts.get(split.prefix) >= minGroup) {
      let group = groups.get(split.prefix);
      if (!group) {
        group = {
          prefix: split.prefix,
          rows: []
        };
        groups.set(split.prefix, group);
        entries.push({
          group
        });
      }
      group.rows.push({
        label: split.rest,
        field
      });
    } else {
      entries.push({
        field
      });
    }
  });
  return {
    entries,
    empty
  };
}

export { PREFIX_RE, customFieldLayout, customFieldLayout as default, splitPrefix };
//# sourceMappingURL=custom-field-layout.js.map
